import { useAuth } from "@/hooks/useAuth";

/**
 * Centralized inspection permission hook.
 *
 * Ensures consistent permission checks across ALL entry points
 * (Inspections page, Tasks page, etc.).
 *
 * See Antipattern 011: Permission Guard Inconsistency Across Entry Points
 *
 * Berechtigungsmatrix:
 * - QM: create=full, delete=full (alle), execute=full
 * - Tester: create=none (5-Rollen-Modell v2.1/TES-1800: führt durch, legt nicht an), delete=limited (nur eigene), execute=limited
 * - Admin/SuperAdmin/Mangelbearbeiter: keine operativen Rechte
 */
export function useInspectionPermissions() {
  const { user, isQualitaetsmanager, roles } = useAuth();

  const isTester = roles.includes("tester");

  // Nur QM (und qm_admin, sobald operativ — TES-1799) darf Prüfungen erstellen.
  // 5-Rollen-Modell v2.1 / TES-1800: der Prüfer FÜHRT DURCH (execute), legt aber NICHT an
  // (inspections:create=none) — sonst zeigt die UI einen Button, der an der RLS mit 42501 scheitert.
  const canCreateInspection = isQualitaetsmanager;

  /**
   * Prüft ob der aktuelle Benutzer eine bestimmte Prüfung löschen darf.
   * - QM: darf alle löschen
   * - Tester: darf nur eigene löschen (created_by === user.id)
   * - Andere: dürfen nicht löschen
   */
  const canDeleteInspection = (inspection: { created_by?: string | null; status?: string }) => {
    // Abgeschlossene oder stornierte Prüfungen dürfen nicht gelöscht werden
    if (inspection.status === "completed" || inspection.status === "cancelled") {
      return false;
    }

    // QM darf alle löschen
    if (isQualitaetsmanager) return true;

    // Tester darf nur eigene löschen
    if (isTester && inspection.created_by === user?.id) return true;

    return false;
  };

  return {
    canCreateInspection,
    canDeleteInspection,
  };
}
